import { formatMention } from "@shared/ui/formatMention";
import { serializeMentions } from "@shared/ui/serializeMentions";
import { tokenizeMentions } from "@shared/ui/tokenizeMentions";

import {
    type AssignmentMap,
    type ElementSettingMap,
    type SettingsPayload,
    type SlideSettingMap,
    type TargetRef,
} from "../SettingsPayload";

import { narrowSkipTargets } from "./narrowSkipTargets";
import { parseMentions } from "./parseMentions";
import { makeElementRef, makeSlideRef, parseTargetRef } from "./targetRef";

/**
 * 旧鍵から新鍵への対応表。走査（`reconcileUids`）が両方を握っている瞬間にだけ作れる。
 * 値が無い旧鍵は「その図形がもうこのファイルに無い」ことを意味する。
 */
export type UidMapping = {
    /** office.js の slide id → アドインが発行したスライドの識別子。 */
    slides: { [officeSlideId: string]: string };
    /** creationId → アドインが発行した要素の識別子。 */
    elements: { [creationId: string]: string };
    /**
     * 新しい識別子 → 表示名。本文へ対象を書き起こすときの見出しに使う。
     * 引けない識別子は種別だけの既定名にするので、渡さなくても移行は成立する。
     */
    names?: { [uid: string]: string };
};

/**
 * v7（PowerPoint 発行の ID を鍵にする）を v8（アドイン発行の識別子を鍵にする）へ移す。
 *
 * 対応の付かない鍵は捨てる。ファイルから消えた図形の設定を残しても、二度と画面に出ず、
 * 生成にも使われないためで、残すと `elements` が単調に膨らむ。
 *
 * **creationId が重複していた分は復元できない。** 本番フォーマットでは 68 種の値が
 * 重複しており、最悪の 1 つは 67 箇所が同じ値を名乗っていた。走査は先に現れた図形へ
 * 元の識別子を残すので、設定もそこへ寄る。どの図形のものだったかという情報は元から
 * 失われており、移行の不備ではない。
 * 設計: docs/notes/設計ノート/2026-08-31 設計ノート アドイン 生成設定の識別子.md
 */
export function migrateToUids(payload: SettingsPayload, mapping: UidMapping): SettingsPayload {
    const slides: SlideSettingMap = {};
    for (const [officeSlideId, setting] of Object.entries(payload.slides)) {
        const uid = mapping.slides[officeSlideId];
        if (uid !== undefined) slides[uid] = setting;
    }

    const elements: ElementSettingMap = {};
    for (const [creationId, setting] of Object.entries(payload.elements)) {
        const uid = mapping.elements[creationId];
        if (uid !== undefined) elements[uid] = setting;
    }

    const assignments: AssignmentMap = {};
    for (const [id, a] of Object.entries(payload.assignments)) {
        const targets: TargetRef[] = [];
        const moved: { [old: string]: TargetRef } = {};
        for (const ref of a.targets) {
            const next = remapRef(ref, mapping);
            if (next === null) continue;
            moved[ref] = next;
            if (targets.indexOf(next) < 0) targets.push(next);
        }
        const skipTargets = (a.skipTargets ?? [])
            .map((ref) => moved[ref])
            .filter((v): v is TargetRef => v !== undefined);

        assignments[id] = {
            ...a,
            targets,
            skipTargets: narrowSkipTargets(skipTargets, targets),
            instruction: writeMentionsIfMissing(
                remapInstruction(a.instruction, moved),
                targets,
                mapping.names
            ),
        };
    }

    return { ...payload, version: 8, slides, elements, assignments };
}

/** 参照 1 件を新しい鍵へ移す。対応が付かなければ null。 */
function remapRef(ref: TargetRef, mapping: UidMapping): TargetRef | null {
    const { kind, officeId } = parseTargetRef(ref);
    if (kind === "slide") {
        const uid = mapping.slides[officeId];
        return uid === undefined ? null : makeSlideRef(uid);
    }
    const uid = mapping.elements[officeId];
    return uid === undefined ? null : makeElementRef(uid);
}

/**
 * 対象を持つのに本文が名指していない担当へ、対象を本文の頭へ書き起こす。
 *
 * v6 は対象を本文とは別の欄で選ばせていたため、移行してきた担当は本文にメンションを
 * 持たない。v7 以降は**本文が対象を決める**ので、そのままにすると本文を 1 文字直した
 * だけで対象が消える。移行の一度きりで本文を整え、以降の編集で壊れないようにする。
 *
 * 名前が引けない対象は種別だけの見出しにする。参照は `ref` が持っているので、表示名が
 * 既定でも書き込み先は失われない。
 *
 * **名前は安全化してから埋める。** 名前の出どころは図形名で、`売上[実績](見込)` のように
 * `](` が並ぶと、書き起こしたメンションがその場から読めない文字列になる（記録 53 と同じ形。
 * 安全化が候補から挿れる経路にしか入っていなかった・記録 72）。安全化は `formatMention` が
 * 内包するので、表記を組む口をそれに一本化してある。
 */
function writeMentionsIfMissing(
    instruction: string,
    targets: TargetRef[],
    names: { [uid: string]: string } | undefined
): string {
    if (targets.length === 0) return instruction;
    if (parseMentions(instruction).length > 0) return instruction;

    const head = targets
        .map((ref) => {
            const { kind, officeId } = parseTargetRef(ref);
            const name = names?.[officeId] ?? (kind === "slide" ? "この枚" : "この枠");
            return formatMention(name, ref);
        })
        .join(" ");
    return instruction.trim().length > 0 ? `${head}\n${instruction}` : head;
}

/**
 * 本文に埋め込まれたメンションを差し替える。
 *
 * 対象は本文が決めるので、`targets` だけ直して本文を放置すると次の編集で元へ戻る。
 * 対応の付かないメンションは表示名だけの平文にする。消すと文が壊れるため。
 *
 * **読み取りと同じ器で分ける。** 以前はここに独自の正規表現を持っていて、表示名に `]` を
 * 許さない古い形のままだった。読み取り側（`tokenizeMentions`）は `#133` で
 * `](` の並びまで許す形に直っており、`[会社名] 表` や `表[1]` のような名前を通す。差し替え
 * だけが追いついていなかったため、そういう名前のメンションは古い参照のまま残り、**次に本文を
 * 編集した瞬間に対象が古い鍵へ戻って、その枠には何も書かれなくなっていた**（記録 72）。
 * トークン列から組み直せば、表示名の規則が増えても差し替えが遅れない。
 *
 * 読み取れた表示名は `](` を含みえないので、埋め直すときに安全化は通さない。通すと既存の
 * 表示名が勝手に全角へ変わる（`formatMention` の注記）。だから戻す口は `serializeMentions`
 * である。
 */
function remapInstruction(instruction: string, moved: { [old: string]: TargetRef }): string {
    return serializeMentions(
        tokenizeMentions(instruction).map((token) => {
            if (token.kind === "text") return token;
            const next = moved[token.ref];
            if (next !== undefined) return { ...token, ref: next };
            // 対応が付かない。表示名だけを平文で残す。名前が無ければメンションの形を残す。
            return token.name.length > 0 ? { kind: "text" as const, text: token.name } : token;
        })
    );
}
